import Http from "../axios/http";
import { getToken } from "../auth/token-store";

/**
 * IIDM-3064 T2 — file downloads without the bearer token in the URL.
 *
 * Contract: IIDM-3056 (buzzworld_api `fc55fb5f`), "FE contract" in
 * `governance/agents/work/IIDM-3056/IMPL.md`. Every download route accepts
 * `Authorization: Bearer <token>`; `?token=` is a one-release fallback.
 *
 * This module is deliberately OUTSIDE the IIDM-2884 `.result` contract of `triggerApi`: a
 * successful download is a file, not a `TransformerResponse` envelope.
 */

export type DownloadResult = { ok: true } | { ok: false; status: number; message: string };

const DEFAULT_MESSAGE = "Download failed. Please try again.";

/** The message shown for each status the 3056 contract documents. */
export function messageForStatus(status: number, serverMessage?: string): string {
  if (status === 403) {
    // 3056: a foreign attachment and an UNKNOWN one answer the same 403, on purpose.
    return "You do not have access to this file.";
  }
  if (status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  if (status === 422) {
    return serverMessage || "The file could not be found.";
  }
  return DEFAULT_MESSAGE;
}

/**
 * Only 401 rejects, so the response interceptor still re-authorizes (IIDM-3064 T3). Every other
 * status RESOLVES and is handled here — in particular 403, which the interceptor would otherwise
 * turn into a full-page navigation to /access-denied.
 */
export const downloadValidateStatus = (status: number): boolean => status !== 401;

/** Read a Blob as text. jsdom 16 has no `Blob.prototype.text`, browsers older than 2020 neither. */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof (blob as any).text === "function") {
    return (blob as any).text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** `result.message` of a JSON envelope carried in a blob body, or undefined. */
async function envelopeMessage(blob: unknown): Promise<string | undefined> {
  if (!(blob instanceof Blob)) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(await readBlobText(blob));
    return parsed && parsed.result && typeof parsed.result.message === "string"
      ? parsed.result.message
      : undefined;
  } catch (e) {
    return undefined;
  }
}

/** `filename=` from a Content-Disposition header, when CORS exposed it. */
export function filenameFromDisposition(header: unknown): string | undefined {
  if (typeof header !== "string") {
    return undefined;
  }
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch (e) {
      // fall through to the plain form
    }
  }
  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
  return plain ? plain[2].trim() : undefined;
}

/** Hand a blob to the browser as a download, then release the object URL. */
function saveBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * GET `apiUrl` (relative, joined to the axios baseURL — IIDM-2883) with the bearer HEADER and
 * save the response as a file. Never throws.
 *
 * Use ONLY on routes whose CORS headers reach the browser (see `tokenNavigationUrl` below).
 */
export async function downloadBlob(apiUrl: string, fallbackFilename: string): Promise<DownloadResult> {
  try {
    const res = await Http.get(apiUrl, {
      responseType: "blob",
      timeout: 30000,
      validateStatus: downloadValidateStatus,
    });
    if (res.status < 200 || res.status >= 300) {
      const serverMessage = await envelopeMessage(res.data);
      return { ok: false, status: res.status, message: messageForStatus(res.status, serverMessage) };
    }
    const headers: any = res.headers || {};
    const filename = filenameFromDisposition(headers["content-disposition"]) || fallbackFilename;
    saveBlob(res.data, filename);
    return { ok: true };
  } catch (err: any) {
    const status = err && err.response && err.response.status ? err.response.status : 0;
    return { ok: false, status, message: messageForStatus(status) };
  }
}

/**
 * ⚠️ TRANSITIONAL — `?token=` navigation, for the routes the BE cannot serve to a cross-origin
 * `fetch` yet: `getInvoicePdf`, `DownloadPdf` (and `ImportPricing`, the quote-items import
 * template and the `ExportData` CSV, which the portal does not call).
 *
 * IIDM-3056 "CORS caveat": those routes stream the file with `header()` + `readfile`/`echo` before
 * the CORS middleware runs, so `Access-Control-Allow-Origin` never reaches the browser and a
 * header-auth `fetch` of any file over 4 KB is blocked. Delete this function, and move its callers
 * to `downloadBlob`, when the IIDM-3056 follow-up "make the 5 raw-stream download routes
 * CORS-safe" lands. The BE's `?token=` fallback (one release) must outlive that switch.
 *
 * The token is read at CLICK time (IIDM-3064 T1), never captured earlier. This is the ONLY place
 * in `src/` that may put the token in a URL; a source guard enforces it.
 */
export function tokenNavigationUrl(apiUrl: string): string {
  const base = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const path = apiUrl.replace(/^\/+/, "");
  const sep = path.indexOf("?") === -1 ? "?" : "&";
  return `${base}/${path}${sep}token=${encodeURIComponent(getToken() || "")}`;
}
